import { h } from 'preact';
import { useState, useEffect } from 'preact/hooks';
import type { Packsize, DosageForm } from '../types';
import { useJsonProvider } from '~/components-shop/JsonProvider';
import { useShopware } from '../shopware/ShopwareContext';
import { getFormattedPrices, checkProductAvailability, formatPrice, getEffectiveCalculatedPrice } from '../shopware/shopwareUtils';
import { URLS } from '~/components-shop/Urls';
import type { Product as ShopwareProduct } from '../shopware/shopwareApi';
import { fetchPdpData, getCountryParamFromUrl, pickPdpPromotion, type PdpVariantData, type PdpPromotion } from '../shopware/pdpData';
import { useEnsureCustomerLogin } from '../shopware/useEnsureCustomerLogin';
import { OrthomolIcon } from '../../components-common/OrthomolIcon';
import { sanitizeHtml } from '../utils/sanitizeHtml';
import { TreuepunkteInfo } from '../common/TreuepunkteInfo';
import { buildGA4Item, pushViewItem, pushAddToCart, pushReservePharmacy } from '../tracking/ecommerceDataLayer';
import { findBestPlanONumber, getDeliveryIntervalTextFromPlan } from '../utils/subscriptionUtils';
import { AboBenefitsList } from '../common/AboBenefitsList';
import { BackInStockModal } from './BackInStockModal';
import { usePromoCode } from '../promo/usePromoCode';
import type { PromoState } from '../promo/usePromoCode';
import { PromoBanner } from '../promo/PromoBanner';
import { PromoOffer } from '../promo/PromoOffer';
import { pdpPromotionToMetadata, pdpPromotionToAppliedState } from '../promo/pdpPromotion';
import { getPromoRecord, setPromoRecord } from '../promo/promoStorage';
import { setFreeShippingThreshold } from '../cart/freeShipping';

interface SelectedDosageForm {
  packsize: Packsize;
  dosageForm: DosageForm;
  shopwareProduct: ShopwareProduct | null;
  subscriptionProduct: ShopwareProduct | null;
  // Promotions + shipping for the selected (main) variant from /pdp-data.
  // Consumed by the Streichpreis / offer rendering (ORT-3089/3060) and free-shipping (ORT-3090).
  pdpData?: PdpVariantData | null;
}

// /pdp-data may omit PIM-synced customFields (e.g. hide_base_price); those flags live in the
// cached embed. Keep the embed's customFields as a base so they survive the /pdp-data overlay,
// with fresh values winning when present.
const mergeEmbedCustomFields = (
  embed: ShopwareProduct | null,
  fresh: ShopwareProduct | null
): ShopwareProduct | null => {
  if (!fresh) return fresh;
  const embedCf = ((embed as any)?.customFields ?? {}) as Record<string, unknown>;
  const freshCf = ((fresh as any)?.customFields ?? {}) as Record<string, unknown>;
  return { ...fresh, customFields: { ...embedCf, ...freshCf } } as ShopwareProduct;
};

// Find packsize by uniquePacksizeId in dosageForm JSON
const findPacksizeByUniquePacksizeId = (
  dosageForms: DosageForm[],
  uniquePacksizeId: number
): { packsize: Packsize; dosageForm: DosageForm } | null => {
  for (const dosageForm of dosageForms) {
    const packsize = dosageForm.packsizes.find((ps) => ps.uniquePacksizeId === uniquePacksizeId);
    if (packsize) {
      return { packsize, dosageForm };
    }
  }
  return null;
};

export const ProductInfo = ({ originalInnerHTML }: { originalInnerHTML: string }) => {
  const { dosageForms, shopwareProductData, baseProductPrices } = useJsonProvider();
  const { openDrawer, addProduct, refreshCart } = useShopware();

  // Customer context for /pdp-data (ORT-3109), shared with the add-to-cart islands.
  const ensureCustomerLogin = useEnsureCustomerLogin();

  const isAboEnabled = dosageForms.data?.shopAboEnabled === true;

  const [selected, setSelected] = useState<SelectedDosageForm | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [purchaseMode, setPurchaseMode] = useState<'single' | 'subscription'>('single');
  const [selectedPlanONumber, setSelectedPlanONumber] = useState<string | null>(null);
  // Stage-2 offer the shopper applied via the Anwenden button (ORT-3060). Local to the PDP:
  // it drives the banner + Streichpreis immediately, while the actual code is persisted for
  // cart-add. Reset on variant change so it never leaks to a variant the code isn't shown for.
  const [appliedOffer, setAppliedOffer] = useState<PdpPromotion | null>(null);
  // ORT-3036: back-in-stock reminder modal, mounted while open (see BackInStockModal)
  const [isReminderOpen, setIsReminderOpen] = useState(false);

  const promoProductContext = selected?.shopwareProduct
    ? {
        id: selected.shopwareProduct.id,
        oNumber: selected.packsize?.oNumber ?? null,
        categoryIds: (selected.shopwareProduct as any).categoryIds ?? null,
        manufacturerId: (selected.shopwareProduct as any).manufacturerId ?? null,
        tagIds: (selected.shopwareProduct as any).tagIds ?? null,
        productStreamIds: (selected.shopwareProduct as any).streamIds ?? null,
        productAnchor: dosageForms.data?.productId != null ? String(dosageForms.data.productId) : null,
        dosageFormAnchor: selected.dosageForm?.id != null ? String(selected.dosageForm.id) : null,
        packsizeAnchor: selected.shopwareProduct.id,
      }
    : null;
  const promoState = usePromoCode(promoProductContext);

  // Check if shop functionality is enabled
  const isShopEnabled = typeof window !== 'undefined' && (window as any).SHOP_ENABLED !== false;

  // Check if ApoNow functionality is enabled
  const isApoNowEnabled = typeof window !== 'undefined' && (window as any).APONOW_ENABLED !== false;

  const shippingInfoUrl = URLS.shippingInfo;

  // Default to subscription mode when -1M variant is available
  useEffect(() => {
    if (isAboEnabled && selected?.subscriptionProduct?.active && selected?.subscriptionProduct?.available) {
      setPurchaseMode('subscription');
    } else {
      setPurchaseMode('single');
    }
  }, [isAboEnabled, selected?.subscriptionProduct]);

  // Persist the free-shipping threshold from /pdp-data so the cart's progress bar can use it
  // instead of the hardcoded 50 EUR (ORT-3090). Only reconcile once we actually have a /pdp-data
  // response: on the intermediate preloaded paint and when /pdp-data fails, pdpData is null, and
  // calling setFreeShippingThreshold(undefined) then would clear a still-valid threshold for the
  // rest of the session and drop the cart back to the 50 EUR default. A present pdpData with no
  // tier legitimately clears (that product has no free-shipping tier).
  useEffect(() => {
    if (!selected?.pdpData) return;
    setFreeShippingThreshold(selected.pdpData.freeShippingThreshold);
  }, [selected?.pdpData]);

  // Update aria-busy attribute whenever isLoading changes
  useEffect(() => {
    const container = document.getElementById('preact-product-infos');
    if (container) {
      container.setAttribute('aria-busy', String(isLoading));
    }
  }, [isLoading]);

  // Get pharmacy button label from the dummy button element
  const dummyButton =
    typeof document !== 'undefined' ? document.getElementById('product-variant-button-pharmacy-label-dummy') : null;
  const pharmacyButtonLabel = dummyButton?.querySelector('span')?.textContent?.trim() || 'In der Apotheke reservieren';

  // Listen to select box changes and fetch product from Shopware
  useEffect(() => {
    if (!dosageForms.data || !Array.isArray(dosageForms.data.dosageForms) || dosageForms.data.dosageForms.length === 0) return;

    const selectElement = document.getElementById('product-variant-select') as HTMLSelectElement | null;
    if (!selectElement) return;

    let isMounted = true;
    let isInitialLoad = true;

    const handleSelectChange = async () => {
      console.log('[eshop] handleSelectChange');
      const selectedOption = selectElement.options[selectElement.selectedIndex];
      if (!selectedOption) {
        setIsLoading(false);
        console.error('[eshop] handleSelectChange: No selected option found');
        return;
      }
      const uniquePacksizeIdString = selectedOption.dataset.uniquePacksizeId;
      const oNumber = selectedOption.dataset.oNumber;

      if (!uniquePacksizeIdString) {
        console.error('[eshop] handleSelectChange: No uniquePacksizeId found');
        setIsLoading(false);
        return;
      }

      // Convert the HTML data attribute string to a number for comparison
      const uniquePacksizeId = Number(uniquePacksizeIdString);
      if (isNaN(uniquePacksizeId)) return;

      const result = findPacksizeByUniquePacksizeId(dosageForms.data!.dosageForms, uniquePacksizeId);
      if (!result) return;

      // Check if this packsize is only available in pharmacy
      const isOnlyInPharmacy = result.packsize.onlyInPharmacy === true;

      // Preloaded (PIM + cached embed) data for an instant, no-flash paint.
      const preloadedProduct =
        isShopEnabled && !isOnlyInPharmacy && oNumber ? (shopwareProductData.data?.products?.[oNumber] ?? null) : null;
      const bestPlanONumber = oNumber
        ? findBestPlanONumber(shopwareProductData.data?.subscriptionPlans, oNumber)
        : null;
      const preloadedSubscription =
        isShopEnabled && !isOnlyInPharmacy && bestPlanONumber
          ? (shopwareProductData.data?.products?.[bestPlanONumber] ?? null)
          : null;

      // On variant changes we paint the preloaded value immediately (fast switch), then overlay
      // /pdp-data with the pulse indicator. On the *initial* load we deliberately do NOT paint
      // the cached value: we hold the loading mask (aria-busy over the whole select + price
      // section) until the first /pdp-data response lands, so the shopper never sees the price
      // jump from the cached embed value to the live one. On failure the mask lifts once the
      // fetch settles (setSelected below runs with the preloaded fallback either way).
      if (preloadedProduct && isMounted && !isInitialLoad) {
        setSelected({ ...result, shopwareProduct: preloadedProduct, subscriptionProduct: preloadedSubscription });
        setSelectedPlanONumber(bestPlanONumber);
        setIsLoading(false);
      } else if (isMounted) {
        setIsLoading(true);
      }

      const country = getCountryParamFromUrl();

      // One consolidated Shopware call for the selected variant + its abo (-1M) sibling,
      // including promotions + shipping. Replaces the two per-variant /product fetches and
      // the JsonProvider batch refresh. `country` is the ORT-3064 test toggle (?country=at)
      // driving countryIso-based pricing/promotions. On failure we keep the preloaded
      // (PIM + cached embed) paint rather than blanking the product.
      let shopwareProduct: ShopwareProduct | null = preloadedProduct;
      let subscriptionProduct: ShopwareProduct | null = preloadedSubscription;
      let pdpData: PdpVariantData | null = null;

      if (isShopEnabled && !isOnlyInPharmacy && oNumber) {
        try {
          const numbers = bestPlanONumber ? [oNumber, bestPlanONumber] : [oNumber];
          // Logged-in: obtain the customer token first, then request personalized /pdp-data.
          // withCustomerToken tracks the actual login result, so a failed login falls back to the
          // anonymous (cacheable) path instead of requesting customer prices with no valid token.
          const withCustomerToken = await ensureCustomerLogin();
          const dataByNumber = await fetchPdpData(numbers, country, { withCustomerToken });
          if (dataByNumber[oNumber]) {
            pdpData = dataByNumber[oNumber];
            shopwareProduct = mergeEmbedCustomFields(preloadedProduct, pdpData.product);
          }
          if (bestPlanONumber && dataByNumber[bestPlanONumber]) {
            subscriptionProduct = mergeEmbedCustomFields(preloadedSubscription, dataByNumber[bestPlanONumber].product);
          }
          console.log('[eshop] /pdp-data product:', shopwareProduct);
          console.log('[eshop] /pdp-data subscription variant:', subscriptionProduct);
        } catch (error) {
          console.error('Failed to fetch /pdp-data from Shopware:', error);
        }
      }

      // Only update state if component is still mounted
      if (isMounted) {
        setSelected({
          ...result,
          shopwareProduct,
          subscriptionProduct,
          pdpData,
        });
        setSelectedPlanONumber(bestPlanONumber);
        setIsLoading(false);

        // Refresh cart after initial product fetch completes to avoid race condition (only if shop is enabled and not onlyInPharmacy)
        if (isShopEnabled && !isOnlyInPharmacy && isInitialLoad) {
          isInitialLoad = false;
          refreshCart().catch((error) => {
            console.error('Failed to fetch cart on product page load:', error);
          });
        }
      }
    };

    // Tab-revisit refresh: when the tab becomes visible again, silently overlay fresh
    // /pdp-data (stock / prices / promotions) onto the current selection, without a skeleton
    // flash or a cart refresh. Replaces the visibilitychange refresh that lived in JsonProvider
    // (catches PDPs left open long enough for the embedded, Cloudflare-cached data to go stale).
    let refreshGeneration = 0;
    const refreshOnVisible = async () => {
      if (document.visibilityState !== 'visible') return;
      const option = selectElement.options[selectElement.selectedIndex];
      const oNumber = option?.dataset.oNumber;
      const uniquePacksizeId = Number(option?.dataset.uniquePacksizeId);
      if (!isShopEnabled || !oNumber || isNaN(uniquePacksizeId)) return;
      const result = findPacksizeByUniquePacksizeId(dosageForms.data!.dosageForms, uniquePacksizeId);
      if (!result || result.packsize.onlyInPharmacy === true) return;

      const bestPlanONumber = findBestPlanONumber(shopwareProductData.data?.subscriptionPlans, oNumber);
      const myGeneration = ++refreshGeneration;
      try {
        const numbers = bestPlanONumber ? [oNumber, bestPlanONumber] : [oNumber];
        const withCustomerToken = await ensureCustomerLogin();
        const dataByNumber = await fetchPdpData(numbers, getCountryParamFromUrl(), { withCustomerToken });
        // Only the latest in-flight refresh applies, and never after unmount.
        if (!isMounted || myGeneration !== refreshGeneration) return;
        setSelected((prev) => {
          if (!prev) return prev;
          const main = dataByNumber[oNumber];
          return {
            ...prev,
            shopwareProduct: main ? mergeEmbedCustomFields(prev.shopwareProduct, main.product) : prev.shopwareProduct,
            subscriptionProduct:
              bestPlanONumber && dataByNumber[bestPlanONumber]
                ? mergeEmbedCustomFields(prev.subscriptionProduct, dataByNumber[bestPlanONumber].product)
                : prev.subscriptionProduct,
            pdpData: main ?? prev.pdpData,
          };
        });
      } catch {
        // Silent: keep the data we already show.
      }
    };

    selectElement.addEventListener('change', handleSelectChange);
    document.addEventListener('visibilitychange', refreshOnVisible);
    handleSelectChange();

    return () => {
      isMounted = false;
      selectElement.removeEventListener('change', handleSelectChange);
      document.removeEventListener('visibilitychange', refreshOnVisible);
    };
  }, [dosageForms, shopwareProductData, refreshCart, isShopEnabled]);

  // Push view_item exactly once per variant the user actually views (PDP load + variant change).
  // Keyed on oNumber so it fires once per variant even though setSelected runs twice per change
  // (preloaded paint, then the /pdp-data overlay). See ORT-3048.
  useEffect(() => {
    if (!selected) return;
    const ga4Item = buildGA4Item(selected.packsize, selected.dosageForm, selected.shopwareProduct, 1);
    if (ga4Item) {
      pushViewItem(ga4Item);
    }
  }, [selected?.packsize.oNumber]);

  // Reset any applied Stage-2 offer when the shopper switches variant (ORT-3060): the offer's code
  // was applied for a specific packsize and may not exist in the new variant's /pdp-data
  // promotions. Carrying it over would keep a stale banner/Streichpreis or suppress the new
  // variant's own offer. Keyed on oNumber so it fires once per variant, not on the intra-variant
  // /pdp-data overlay (which keeps the same oNumber).
  useEffect(() => {
    setAppliedOffer(null);
  }, [selected?.packsize.oNumber]);

  if (dosageForms.error) {
    return null;
  }

  if (!dosageForms.data || !Array.isArray(dosageForms.data.dosageForms) || dosageForms.data.dosageForms.length === 0) {
    return null;
  }

  // While loading initial product, render placeholder skeleton that matches the structure
  if (!selected) {
    // split original HTML into pieces, only way to prevent layout shift 🤷‍♂️
    const parser = new DOMParser();
    const doc = parser.parseFromString(originalInnerHTML, 'text/html');
    const priceInfo = doc.querySelector('.product-variant-priceinfo')?.outerHTML || '';
    const buttons = doc.querySelector('.product-variant-buttons')?.outerHTML || '';
    const additionalInfo = doc.querySelector('.product-variant-additional-info')?.outerHTML || '';

    return (
      <div className="d-flex flex-column gap-4">
        <div dangerouslySetInnerHTML={{ __html: sanitizeHtml(priceInfo) }} />
        <div dangerouslySetInnerHTML={{ __html: sanitizeHtml(buttons) }} />
        <div dangerouslySetInnerHTML={{ __html: sanitizeHtml(additionalInfo) }} />
      </div>
    );
  }

  // Get data from Shopware product
  const shopwareProduct = selected.shopwareProduct;
  const subscriptionProduct = selected.subscriptionProduct;
  const hideBasePrice = (shopwareProduct?.customFields as any)?.hide_base_price === true;

  const { unitPrice: priceItem, referencePrice: priceUnit, listPrice } = getFormattedPrices(shopwareProduct);

  // A locally applied Stage-2 offer (Anwenden) takes the same 'applied' shape as a Stage-1
  // newsletter code, so the banner + Streichpreis logic below is identical for both.
  const effectivePromoState: PromoState = appliedOffer ? pdpPromotionToAppliedState(appliedOffer) : promoState;

  // Promo Streichpreis (ORT-3089). The discounted price comes from /pdp-data's per-promotion
  // promotionPrices (effectivePromotionPrice = calculatedPrices[0] else calculatedPrice). We
  // preview what the cart will charge by considering two kinds of priceOnPdp promotions:
  //   - auto-firing ones (useCodes:false) that apply with no action at all, and
  //   - the promotion for an active code (newsletter deep link or applied Stage-2 offer).
  // Among the applicable ones we show the best (lowest) price. This approximates Shopware's cart
  // outcome but does not model promotion stacking/exclusivity — the cart stays the source of truth.
  // Only treat the code as active FOR PRICING on this product when it is not explicitly out of
  // scope. A newsletter code scoped to other products (?pron=, ORT-3077 A -> eligibleForThisProduct
  // === false) must not light up its /pdp-data Streichpreis here, even if Shopware scopes the
  // promotion more broadly; the banner still shows the "gilt für andere Produkte" note. Auto-firing
  // promotions (!useCodes, e.g. a product's own campaign) are unaffected. (Customer feedback
  // ORT-3077, comment 212490.)
  const activePromoCode =
    effectivePromoState.kind === 'applied' && effectivePromoState.eligibleForThisProduct !== false
      ? effectivePromoState.code
      : null;
  const applicablePromotions = (selected.pdpData?.promotions ?? []).filter(
    (p) =>
      p.priceOnPdp &&
      p.effectivePromotionPrice != null &&
      (!p.useCodes || (activePromoCode != null && p.code === activePromoCode))
  );
  const bestPromotion = applicablePromotions.reduce<PdpPromotion | null>(
    (best, p) =>
      best == null || p.effectivePromotionPrice!.unitPrice < best.effectivePromotionPrice!.unitPrice ? p : best,
    null
  );
  const promoDiscountedPrice = bestPromotion?.effectivePromotionPrice
    ? `${formatPrice(bestPromotion.effectivePromotionPrice.unitPrice)} €`
    : null;

  // ORT-3110: enrich the applied banner with the Shopware-maintained userDescription (rich-text
  // headline) and priceOnPdp (subline variant) from the /pdp-data promotion matching the active
  // code. Covers newsletter Stage-1 codes too (an applied Stage-2 offer already carries these via
  // pdpPromotionToAppliedState — the merge below is idempotent for it). Undefined when no matching
  // promotion is in /pdp-data, so the composed fallback text stays.
  const activeCodePromotion = activePromoCode
    ? (selected.pdpData?.promotions ?? []).find((p) => p.code === activePromoCode) ?? null
    : null;
  const enrichedPromoState: PromoState =
    effectivePromoState.kind === 'applied' && activeCodePromotion
      ? {
          ...effectivePromoState,
          metadata: {
            ...effectivePromoState.metadata,
            userDescription:
              effectivePromoState.metadata.userDescription ?? (activeCodePromotion.userDescription || null),
            // priceOnPdp only when the matched promotion actually yields a Streichpreis, so the
            // "Rabatt wird automatisch abgezogen" subline never claims a discount the price block
            // doesn't show (ORT-3110).
            priceOnPdp:
              effectivePromoState.metadata.priceOnPdp ??
              (activeCodePromotion.priceOnPdp && activeCodePromotion.effectivePromotionPrice != null),
          },
        }
      : effectivePromoState;

  // Single-purchase display: a promo Streichpreis takes precedence over the rule-based
  // listPrice (the promo price is the final price; the normal price becomes the struck original).
  const singleStrike = promoDiscountedPrice ? priceItem : listPrice;
  const singleMain = promoDiscountedPrice ?? priceItem;

  // Subscription availability: enable if plan variant exists, is active and available in Shopware (ignore stock for abo products)
  const isSubscriptionAvailable = isAboEnabled && !!subscriptionProduct?.active && !!subscriptionProduct?.available;

  // Subscription price from the real plan variant product
  const isSubscriptionMode = isSubscriptionAvailable && purchaseMode === 'subscription';
  const {
    unitPrice: subscriptionPriceItem,
    referencePrice: subscriptionPriceUnit,
  } = getFormattedPrices(subscriptionProduct);

  // Use base product price from lookup table as Streichpreis for plan items
  const baseONumber = selectedPlanONumber
    ? shopwareProductData.data?.subscriptionPlans?.[selectedPlanONumber]?.baseONumber
    : undefined;
  const basePrice = baseONumber ? baseProductPrices.data?.[baseONumber] : undefined;
  const subPrice = getEffectiveCalculatedPrice(subscriptionProduct)?.unitPrice;
  const hasSubscriptionDiscount = basePrice != null && subPrice != null && basePrice > subPrice;
  const subscriptionListPrice = hasSubscriptionDiscount
    ? `${formatPrice(basePrice)} €`
    : null;

  const rawSavings =
    hasSubscriptionDiscount && basePrice > 0
      ? Math.round(((basePrice - subPrice) / basePrice) * 100)
      : null;
  const savingsPercent =
    rawSavings != null && Number.isFinite(rawSavings) && rawSavings >= 1 ? rawSavings : null;

  const deliveryTimeText = shopwareProduct?.deliveryTime?.name ?? '2 - 3 Tage';

  // Check product availability in Shopware
  const isAvailableInShop = checkProductAvailability(shopwareProduct);

  // Check if this packsize is only available in pharmacy
  const isOnlyInPharmacy = selected.packsize.onlyInPharmacy === true;

  // Determine if shop features should be shown (respecting both global shop enabled flag and onlyInPharmacy flag)
  const showShopFeatures = isShopEnabled && !isOnlyInPharmacy;

  // Stage-2 offer (ORT-3060): the top visibleOnPdp promotion for this product, shown with an
  // Anwenden button — but only when no code is active yet (a Stage-1 newsletter code or an
  // already-applied offer takes precedence and renders the banner instead).
  // ORT-3077 B: a newsletter deep link with ?prooff=0 suppresses competing Stage-2 offers while
  // its code is remembered, so the newsletter code is not drowned out.
  const suppressOffers = getPromoRecord()?.suppressOffers === true;
  const offerPromotion =
    showShopFeatures && !suppressOffers && !appliedOffer && promoState.kind !== 'applied' && selected.pdpData
      ? pickPdpPromotion(selected.pdpData.promotions)
      : null;
  const productAnchor = dosageForms.data?.productId != null ? String(dosageForms.data.productId) : null;

  const handleApplyOffer = (promo: PdpPromotion) => {
    // Analog to Stage 1: persist the code so the next add-to-cart attaches it (the cart layer
    // reads getPromoRecord); the price resolves in the cart. Local state lights up the banner
    // and Streichpreis immediately.
    setPromoRecord({ code: promo.code, scope: 'p', anchor: productAnchor, applyMode: 'add' });
    setAppliedOffer(promo);
  };

  // Determine button visibility and states
  const showPharmacy = isApoNowEnabled && !!selected.packsize.apoNowUrl;
  const pharmacyUrl = selected.packsize.apoNowUrl || '#';

  //console.log('[eshop] XXXXX', selected.packsize.apoNowUrl);

  // Determine which product to add to cart based on purchase mode
  const cartProduct = isSubscriptionMode && subscriptionProduct ? subscriptionProduct : shopwareProduct;

  const handleAddToCart = async () => {
    if (!cartProduct?.id) {
      console.error('Cannot add to cart: No Shopware product ID available');
      alert('Fehler: Produkt konnte nicht hinzugefügt werden');
      return;
    }

    try {
      openDrawer();
      // Pass the base product's o-number so a ?pron=-scoped code is only attached when this product
      // is in the newsletter's list (ORT-3077). Uses the packsize o-number, matching the banner's
      // eligibility, so an abo variant's differing productNumber does not falsely gate it out.
      await addProduct(cartProduct.id, 1, selected.packsize?.oNumber ?? null);
      console.log(
        '[eshop] Successfully added to cart:',
        cartProduct.id,
        isSubscriptionMode ? '(subscription)' : '(single)'
      );

      // Push add_to_cart event to dataLayer
      const ga4Item = buildGA4Item(selected.packsize, selected.dosageForm, cartProduct, 1);
      if (ga4Item) {
        pushAddToCart(ga4Item);
      }
    } catch (error) {
      console.error('Failed to add to cart:', error);
      alert('Fehler beim Hinzufügen zum Warenkorb');
    }
  };

  return (
    <div className="d-flex flex-column gap-4">
      <PromoBanner state={enrichedPromoState} />
      {offerPromotion && (
        <PromoOffer
          metadata={pdpPromotionToMetadata(offerPromotion)}
          onApply={() => handleApplyOffer(offerPromotion)}
        />
      )}
      {/* Purchase Options - Subscription Selector */}
      {isAboEnabled && showShopFeatures && shopwareProduct && (
        <div className="purchase-options">
          <span className="purchase-options-label">Kaufmöglichkeiten</span>
          <label className={`purchase-option${purchaseMode === 'single' ? ' purchase-option--active' : ''}`}>
            <span className="purchase-option-header">
              <input
                type="radio"
                name="purchase-mode"
                checked={purchaseMode === 'single'}
                onChange={() => setPurchaseMode('single')}
              />
              <span className="purchase-option-name-badge">
                <span className="purchase-option-name">Einmalige Lieferung</span>
              </span>
              <span className="purchase-option-price">
                {singleStrike && <s className="price-original">{singleStrike}</s>}
                <span className={singleStrike ? 'price-discounted' : ''}>{singleMain}</span>
              </span>
            </span>
          </label>
          <label
            className={`purchase-option${purchaseMode === 'subscription' ? ' purchase-option--active' : ''}${!isSubscriptionAvailable ? ' purchase-option--disabled' : ''}`}
          >
            <span className="purchase-option-header">
              <input
                type="radio"
                name="purchase-mode"
                checked={purchaseMode === 'subscription'}
                onChange={() => setPurchaseMode('subscription')}
                disabled={!isSubscriptionAvailable}
              />
              <span className="purchase-option-name-badge">
                <span className="purchase-option-name">Im Abo</span>
                {subscriptionProduct && savingsPercent != null && (
                  <span className="purchase-option-badge">Spare {savingsPercent}%</span>
                )}
              </span>
              {subscriptionProduct && (
                <span className="purchase-option-price">
                  {subscriptionListPrice && <s className="price-original">{subscriptionListPrice}</s>}
                  <span className={subscriptionListPrice ? 'price-discounted' : ''}>{subscriptionPriceItem}</span>
                </span>
              )}
            </span>
            {purchaseMode === 'subscription' && (
              <div className="purchase-option-details">
                <AboBenefitsList
                  deliveryText={getDeliveryIntervalTextFromPlan(
                    shopwareProductData.data?.subscriptionPlans,
                    selectedPlanONumber
                  )}
                />
              </div>
            )}
          </label>
        </div>
      )}

      {/* Price Information */}
      {showShopFeatures && shopwareProduct && (
        <p className="product-variant-priceinfo mb-0 gap-2 px-3 lh-1">
          <strong className="text-nowrap">
            {isSubscriptionMode
              ? subscriptionListPrice && <s className="price-original me-2">{subscriptionListPrice}</s>
              : singleStrike && <s className="price-original me-2">{singleStrike}</s>}
            <span
              id="product-variant-price-item"
              className={(isSubscriptionMode ? subscriptionListPrice : singleStrike) ? 'price-discounted' : ''}
            >
              {isSubscriptionMode ? subscriptionPriceItem : singleMain}
            </span>
          </strong>
          <span className="product-variant-priceinfo-unitprice">
            {!hideBasePrice && (isSubscriptionMode ? subscriptionPriceUnit : priceUnit) && (
              <>
                <span id="product-variant-price-unit" className="text-nowrap">
                  {isSubscriptionMode ? subscriptionPriceUnit : priceUnit}
                </span>{' '}
              </>
            )}
            inkl. MwSt. {isSubscriptionMode ? 'inkl.' : 'zzgl.'} <a href={shippingInfoUrl}>Versand</a>
          </span>
        </p>
      )}

      {/* Action Buttons */}
      <div className="product-variant-buttons d-flex flex-column flex-md-row flex-wrap gap-3">
        {/* Add to Cart Button */}
        {showShopFeatures && (
          <button
            id="product-variant-button-add-to-cart"
            className={`btn ${isAvailableInShop ? 'btn-primary' : 'btn-secondary'} justify-content-center`}
            onClick={handleAddToCart}
            disabled={!isAvailableInShop}
          >
            {isAvailableInShop ? (
              <span>In den Warenkorb</span>
            ) : (
              <span className="out-of-stock">Bald wieder verfügbar</span>
            )}
          </button>
        )}

        {/* Reserve at Pharmacy Button */}
        {showPharmacy && (
          <a
            id="product-variant-button-pharmacy"
            href={pharmacyUrl}
            target="_blank"
            rel="noopener noreferrer"
            className={`btn ${showShopFeatures && isAvailableInShop ? 'btn-secondary' : 'btn-primary'} justify-content-center`}
            onClick={() => {
              // Push reserve_pharmacy event to dataLayer
              const ga4Item = buildGA4Item(selected.packsize, selected.dosageForm, selected.shopwareProduct, 1);
              if (ga4Item) {
                pushReservePharmacy(ga4Item);
              }
            }}
          >
            <span>{pharmacyButtonLabel}</span>
          </a>
        )}

        {/* ORT-3036: back-in-stock reminder. Only where the shop would sell this variant (shop on, not
          pharmacy-only, a Shopware product exists) but it is out of stock. Link text per Jira (Riccardo, 2026-08-28). */}
        {showShopFeatures && shopwareProduct && !isAvailableInShop && (
          <div className="mt-n2 w-100 order-1">
            <button
              type="button"
              id="product-variant-button-back-in-stock"
              className="btn small text-primary gap-1 py-1 border-0"
              onClick={() => setIsReminderOpen(true)}
            >
              <OrthomolIcon name="bell-24" size="16" />
              <span>Benachrichtigen bei Verfügbarkeit</span>
            </button>
            {isReminderOpen && (
              <BackInStockModal
                oNumber={selected.packsize.oNumber}
                dosageFormName={selected.dosageForm.name}
                packsizeLabel={selected.packsize.size}
                onClose={() => setIsReminderOpen(false)}
              />
            )}
          </div>
        )}
      </div>

      {/* Additional Information */}
      {/* Only show the grey box if there's content to display */}
      {(isAvailableInShop || selected.packsize.excludeFromMyOrthomolLoyaltyPoints !== true) && (
        <div id="product-variant-additional-info" className="product-variant-additional-info">
          <div className="small bg-gray-300 p-3 d-flex flex-column gap-4">
            {/* Availability Info */}
            {isAvailableInShop && (
              <div id="product-variant-additional-info-availability" className="mx-1 d-flex gap-3">
                <span className="text-primary">
                  <OrthomolIcon name="delivery-van-24" />
                </span>
                <p className="d-flex flex-column mb-0 text-available">
                  <strong>Sofort verfügbar</strong>
                  Lieferzeit {deliveryTimeText}
                </p>
              </div>
            )}

            {/* Loyalty Points Info */}
            <TreuepunkteInfo excludeFromLoyaltyPoints={selected.packsize.excludeFromMyOrthomolLoyaltyPoints === true} />
          </div>
        </div>
      )}
    </div>
  );
};
